import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Star, TrendingDown, TrendingUp, ChevronDown, X, Square } from 'lucide-react';
import { useInstrument } from '@/api/instruments/use-instruments';
import { useCurrentInstrumentPrice } from '@/hooks/useCurrentInstrumentPrice';
import { useTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import { usePriceChange } from '@/hooks/usePriceChange';
import { useFavorites } from '@/hooks/useFavorites';
import {
    useTrades,
    useCancelTrade,
    useCloseTrade,
    usePartialCloseTrade,
} from '@/api/trades/use-trades';
import MobileOrderForm from '@/components/mobile/MobileOrderForm';
import KLineProChart from '@/components/KLineProChart';
import PriceDisplayWithFlash from '@/components/shared/PriceDisplayWithFlash';
import { Loader2 } from 'lucide-react';
import { formatCurrencyAmount } from '@/utils/formatters';
import { useSnackbar } from 'notistack';
import { usePriceFlash } from '@/hooks/usePriceFlash';

const MobileTradingPage: React.FC = () => {
    const { t } = useTranslation('mobile');
    const navigate = useNavigate();
    const { instrumentId } = useParams<{ instrumentId: string }>();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [activeTimeframe, setActiveTimeframe] = useState('1h');
    const [activeTab, setActiveTab] = useState<'Pending' | 'Open' | 'History'>('Pending');
    const [orderFormOpen, setOrderFormOpen] = useState(false);
    const [orderDirection, setOrderDirection] = useState<'buy' | 'sell'>('buy');

    // Dialog states
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [selectedTradeToCancel, setSelectedTradeToCancel] = useState<any>(null);
    const [partialCloseDialogOpen, setPartialCloseDialogOpen] = useState(false);
    const [selectedTradeToPartialClose, setSelectedTradeToPartialClose] = useState<any>(null);
    const [partialCloseLots, setPartialCloseLots] = useState('');

    // Mutations
    const cancelTradeMutation = useCancelTrade();
    const closeTradeMutation = useCloseTrade();
    const partialCloseTradeMutation = usePartialCloseTrade();

    // Fetch trades based on active tab
    const { data: pendingTradesData, isLoading: pendingLoading } = useTrades({
        status: 'pending',
        per_page: 50,
    });

    const { data: openTradesData, isLoading: openLoading } = useTrades({
        status: 'open',
        per_page: 50,
    });

    const { data: closedTradesData, isLoading: historyLoading } = useTrades({
        status: 'closed',
        per_page: 50,
    });

    const pendingTrades = pendingTradesData?.trades || [];
    const openTrades = openTradesData?.trades || [];
    const closedTrades = closedTradesData?.trades || [];

    // Get instrument from location state or fetch by ID
    const instrument = location.state?.instrument || null;
    const finalInstrumentId = instrumentId || instrument?.id;

    const { data: instrumentData, isLoading: isLoadingInstrument } = useInstrument(
        finalInstrumentId || ''
    );
    const { price: currentPrice, isLoading: isPriceLoading } = useCurrentInstrumentPrice(
        finalInstrumentId || '' // updated via WebSocket (usePriceSocket)
    );
    const { data: tradingAccounts } = useTradingAccounts();
    const selectedAccount = tradingAccounts?.accounts?.[0] || null;
    const { isFavorite, toggleFavorite } = useFavorites();

    // Calculate price change
    const priceValue = currentPrice?.bid || 0;
    // Enable 24h change calculation on trading page (detail view)
    const { data: priceChangeData } = usePriceChange(finalInstrumentId, priceValue, true);
    const changePercent = priceChangeData?.changePercent || 0;

    // Get real-time price direction for trend icon (matches price flash behavior)
    const { direction: priceDirection } = usePriceFlash(priceValue);

    const symbol = instrument?.symbol || instrumentData?.symbol || 'N/A';
    const price = priceValue;
    const favorite = finalInstrumentId ? isFavorite(finalInstrumentId) : false;

    // Handle cancel trade
    const handleCancelTrade = (trade: any) => {
        setSelectedTradeToCancel(trade);
        setCancelDialogOpen(true);
    };

    const confirmCancelTrade = async () => {
        if (!selectedTradeToCancel) return;
        try {
            await cancelTradeMutation.mutateAsync({
                id: selectedTradeToCancel.id,
                data: { reason: 'manual' },
            });
            enqueueSnackbar(t('orderCancelledSuccess'), { variant: 'success' });
            setCancelDialogOpen(false);
            setSelectedTradeToCancel(null);
        } catch (error: any) {
            const message = error?.response?.data?.message || t('failedToCancelOrder');
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    // Handle full close trade
    const handleCloseTrade = async (trade: any) => {
        try {
            await closeTradeMutation.mutateAsync({
                id: trade.id,
                data: { reason: 'manual' },
            });
            enqueueSnackbar(t('positionClosedSuccess'), { variant: 'success' });
        } catch (error: any) {
            const message = error?.response?.data?.message || t('failedToClosePosition');
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    // Handle partial close trade
    const handlePartialCloseClick = (trade: any) => {
        setSelectedTradeToPartialClose(trade);
        setPartialCloseLots('');
        setPartialCloseDialogOpen(true);
    };

    const confirmPartialClose = async () => {
        if (!selectedTradeToPartialClose) return;
        const lots = parseFloat(partialCloseLots);
        if (!lots || lots <= 0) {
            enqueueSnackbar(t('enterValidLots'), { variant: 'error' });
            return;
        }
        const totalLots = Number(selectedTradeToPartialClose.lot_size || 0);
        if (lots >= totalLots) {
            enqueueSnackbar(t('partialCloseLotsLessThanTotal'), {
                variant: 'error',
            });
            return;
        }
        try {
            await partialCloseTradeMutation.mutateAsync({
                id: selectedTradeToPartialClose.id,
                data: {
                    close_lots: lots,
                    reason: 'partial_close',
                },
            });
            enqueueSnackbar(t('positionPartiallyClosedSuccess'), { variant: 'success' });
            setPartialCloseDialogOpen(false);
            setSelectedTradeToPartialClose(null);
            setPartialCloseLots('');
        } catch (error: any) {
            const message = error?.response?.data?.message || t('failedToPartiallyClosePosition');
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    if (isLoadingInstrument && !instrument) {
        return (
            <div className="min-h-screen text-foreground flex items-center justify-center pb-32">
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen text-foreground">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-border">
                <button onClick={() => navigate('/market')}>
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-2">
                    <span className="font-semibold">{symbol}</span>
                    <ChevronDown className="w-4 h-4" />
                </div>
                <button
                    onClick={() => {
                        if (finalInstrumentId) {
                            toggleFavorite(finalInstrumentId);
                        }
                    }}
                    className="p-1 hover:bg-foreground/10 rounded transition-colors"
                >
                    <Star
                        className={`w-6 h-6 transition-colors ${
                            favorite ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'
                        }`}
                    />
                </button>
            </div>

            {/* Price Info */}
            <div className="px-4 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <PriceDisplayWithFlash
                        price={price}
                        className="text-3xl font-bold"
                        formatPrice={p => p.toFixed(5)}
                    />
                    {/* Show trend icon based on real-time price movement, fallback to 24h change */}
                    {priceDirection === 'up' || (priceDirection === null && changePercent >= 0) ? (
                        <TrendingUp className="w-6 h-6 text-emerald-500" />
                    ) : (
                        <TrendingDown className="w-6 h-6 text-red-500" />
                    )}
                </div>
            </div>

            {/* Chart Area */}
            <div
                className="px-4 py-4 bg-foreground/5"
                style={{ minHeight: '400px', height: '400px' }}
            >
                {finalInstrumentId ? (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            minHeight: '400px',
                            overflowX: 'hidden',
                        }}
                    >
                        <KLineProChart instrumentId={finalInstrumentId} symbol={symbol} />
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                        {t('selectInstrumentForChart')}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-border">
                {[
                    { key: 'Pending' as const, label: t('pending') },
                    { key: 'Open' as const, label: t('openTab') },
                    { key: 'History' as const, label: t('historyTab') },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`flex-1 py-3 text-sm font-medium ${
                            activeTab === key
                                ? 'text-primary border-b-2 border-primary'
                                : 'text-muted-foreground'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Trade Lists Content */}
            <div className="pb-24">
                {activeTab === 'Pending' && (
                    <div className="px-4 py-4">
                        {pendingLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin" />
                            </div>
                        ) : pendingTrades.length === 0 ? (
                            <div className="text-center text-muted-foreground py-8">
                                {t('noPendingOrders')}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {pendingTrades.map((trade: any) => (
                                    <div key={trade.id} className="trader-glass rounded-2xl p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className={`px-2 py-1 rounded text-xs font-semibold ${
                                                        trade.side === 'buy'
                                                            ? 'trader-pill-positive'
                                                            : 'trader-pill-negative'
                                                    }`}
                                                >
                                                    {trade.side?.toUpperCase() || 'N/A'}
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-sm">
                                                        {trade.instrument?.symbol || 'N/A'}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {trade.type?.toUpperCase() || 'MARKET'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-semibold">
                                                    {Number(trade.lot_size || 0).toFixed(2)}{' '}
                                                    {t('lots')}
                                                </div>
                                                {trade.entry_price && (
                                                    <div className="text-xs text-muted-foreground">
                                                        @ {Number(trade.entry_price).toFixed(5)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {trade.expires_at && (
                                            <div className="text-xs text-muted-foreground pt-2 border-t border-border mb-2">
                                                {t('expires')}:{' '}
                                                {new Date(trade.expires_at).toLocaleString()}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => handleCancelTrade(trade)}
                                            disabled={cancelTradeMutation.isPending}
                                            className="w-full mt-2 py-2 trader-pill-negative hover:opacity-80 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            <X className="w-4 h-4" />
                                            {cancelTradeMutation.isPending
                                                ? t('cancelling')
                                                : t('cancelOrder')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'Open' && (
                    <div className="px-4 py-4">
                        {openLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin" />
                            </div>
                        ) : openTrades.length === 0 ? (
                            <div className="text-center text-muted-foreground py-8">
                                {t('noOpenPositions')}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {openTrades.map((trade: any) => {
                                    const pnl = Number(trade.unrealized_pnl || 0);
                                    const pnlPositive = pnl >= 0;
                                    return (
                                        <div
                                            key={trade.id}
                                            className="trader-glass rounded-2xl p-4"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className={`px-2 py-1 rounded text-xs font-semibold ${
                                                            trade.side === 'buy'
                                                                ? 'trader-pill-positive'
                                                                : 'trader-pill-negative'
                                                        }`}
                                                    >
                                                        {trade.side?.toUpperCase() || 'N/A'}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-sm">
                                                            {trade.instrument?.symbol || 'N/A'}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {Number(trade.lot_size || 0).toFixed(2)}{' '}
                                                            lots
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    className={`text-right ${
                                                        pnlPositive
                                                            ? 'text-trader-positive'
                                                            : 'text-trader-negative'
                                                    }`}
                                                >
                                                    <div className="text-sm font-bold">
                                                        {pnlPositive ? '+' : ''}
                                                        {formatCurrencyAmount(
                                                            pnl,
                                                            trade.trading_account?.base_currency ||
                                                                'USD'
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {t('pnl')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground pt-2 border-t border-border mb-2">
                                                <div>
                                                    <span>{t('entry')}: </span>
                                                    <span className="text-foreground">
                                                        {trade.entry_price
                                                            ? Number(trade.entry_price).toFixed(5)
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>{t('current')}: </span>
                                                    <span className="text-foreground">
                                                        {trade.current_price
                                                            ? Number(trade.current_price).toFixed(5)
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handlePartialCloseClick(trade)}
                                                    disabled={partialCloseTradeMutation.isPending}
                                                    className="flex-1 py-2 bg-primary/15 hover:bg-primary/25 text-primary rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {t('partialClose')}
                                                </button>
                                                <button
                                                    onClick={() => handleCloseTrade(trade)}
                                                    disabled={closeTradeMutation.isPending}
                                                    className="flex-1 py-2 trader-pill-negative hover:opacity-80 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                                >
                                                    <Square className="w-4 h-4" />
                                                    {closeTradeMutation.isPending
                                                        ? t('closing')
                                                        : t('close')}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'History' && (
                    <div className="px-4 py-4">
                        {historyLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin" />
                            </div>
                        ) : closedTrades.length === 0 ? (
                            <div className="text-center text-muted-foreground py-8">
                                {t('noTradeHistory')}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {closedTrades.map((trade: any) => {
                                    const pnl = Number(trade.realized_pnl || 0);
                                    const pnlPositive = pnl >= 0;
                                    return (
                                        <div
                                            key={trade.id}
                                            className="trader-glass rounded-2xl p-4"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className={`px-2 py-1 rounded text-xs font-semibold ${
                                                            trade.side === 'buy'
                                                                ? 'trader-pill-positive'
                                                                : 'trader-pill-negative'
                                                        }`}
                                                    >
                                                        {trade.side?.toUpperCase() || 'N/A'}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-sm">
                                                            {trade.instrument?.symbol || 'N/A'}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {Number(trade.lot_size || 0).toFixed(2)}{' '}
                                                            {t('lots')}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    className={`text-right ${
                                                        pnlPositive
                                                            ? 'text-trader-positive'
                                                            : 'text-trader-negative'
                                                    }`}
                                                >
                                                    <div className="text-sm font-bold">
                                                        {pnlPositive ? '+' : ''}
                                                        {formatCurrencyAmount(
                                                            pnl,
                                                            trade.trading_account?.base_currency ||
                                                                'USD'
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {t('pnl')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground pt-2 border-t border-border">
                                                <div>
                                                    <span>{t('entry')}: </span>
                                                    <span className="text-foreground">
                                                        {trade.entry_price
                                                            ? Number(trade.entry_price).toFixed(5)
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>{t('exit')}: </span>
                                                    <span className="text-foreground">
                                                        {trade.exit_price
                                                            ? Number(trade.exit_price).toFixed(5)
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            {trade.closed_at && (
                                                <div className="text-xs text-muted-foreground pt-2">
                                                    {t('closed')}:{' '}
                                                    {new Date(trade.closed_at).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="fixed bottom-0 left-0 right-0 p-4 trader-glass-strong border-t border-border z-40">
                <div className="flex gap-3 max-w-md mx-auto">
                    <button
                        onClick={() => {
                            setOrderDirection('buy');
                            setOrderFormOpen(true);
                        }}
                        className="flex-1 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition-colors"
                    >
                        {t('buy')}
                    </button>
                    <button
                        onClick={() => {
                            setOrderDirection('sell');
                            setOrderFormOpen(true);
                        }}
                        className="flex-1 py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-colors"
                    >
                        {t('sell')}
                    </button>
                </div>
            </div>

            {/* Order Form Modal */}
            {finalInstrumentId && (
                <MobileOrderForm
                    isOpen={orderFormOpen}
                    onClose={() => setOrderFormOpen(false)}
                    selectedPair={symbol}
                    selectedInstrumentId={finalInstrumentId}
                    realTimePrice={currentPrice}
                    selectedAccount={selectedAccount}
                    tradingAccounts={tradingAccounts?.accounts}
                    onOrderSuccess={() => {
                        setOrderFormOpen(false);
                        // Optionally refresh data or navigate
                    }}
                    isPricesLoading={isPriceLoading}
                    instrumentType={
                        (instrumentData?.instrument_type?.primary_type as any) || 'forex'
                    }
                    instrumentData={
                        instrumentData
                            ? {
                                  min_lot: instrumentData.min_lot,
                                  max_lot: instrumentData.max_lot,
                                  max_leverage: instrumentData.max_leverage,
                                  leverage: instrumentData.leverage,
                              }
                            : undefined
                    }
                    direction={orderDirection}
                />
            )}

            {/* Cancel Trade Confirmation Dialog */}
            {cancelDialogOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
                    <div className="w-full trader-glass-strong rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">{t('cancelOrderTitle')}</h3>
                        <p className="text-muted-foreground mb-6">{t('cancelOrderConfirm')}</p>
                        {selectedTradeToCancel && (
                            <div className="trader-glass rounded-xl p-4 mb-6">
                                <div className="text-sm text-muted-foreground mb-2">
                                    {t('orderDetails')}
                                </div>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">
                                            {t('symbol')}:
                                        </span>
                                        <span className="text-foreground">
                                            {selectedTradeToCancel.instrument?.symbol || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('side')}:</span>
                                        <span className="text-foreground">
                                            {selectedTradeToCancel.side?.toUpperCase() || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">{t('lots')}:</span>
                                        <span className="text-foreground">
                                            {Number(selectedTradeToCancel.lot_size || 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setCancelDialogOpen(false);
                                    setSelectedTradeToCancel(null);
                                }}
                                className="flex-1 py-3 trader-glass hover:bg-foreground/10 rounded-lg font-medium transition-colors"
                            >
                                {t('noKeepOrder')}
                            </button>
                            <button
                                onClick={confirmCancelTrade}
                                disabled={cancelTradeMutation.isPending}
                                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {cancelTradeMutation.isPending
                                    ? t('cancelling')
                                    : t('yesCancelOrder')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Partial Close Dialog */}
            {partialCloseDialogOpen && selectedTradeToPartialClose && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
                    <div className="w-full trader-glass-strong rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">{t('partialCloseTitle')}</h3>
                        <div className="trader-glass rounded-xl p-4 mb-6">
                            <div className="text-sm text-muted-foreground mb-2">
                                {t('positionDetails')}
                            </div>
                            <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">{t('symbol')}:</span>
                                    <span className="text-foreground">
                                        {selectedTradeToPartialClose.instrument?.symbol || 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">{t('totalLots')}:</span>
                                    <span className="text-foreground">
                                        {Number(selectedTradeToPartialClose.lot_size || 0).toFixed(
                                            2
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm text-muted-foreground mb-2">
                                {t('lotsToClose')}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max={Number(selectedTradeToPartialClose.lot_size || 0) - 0.01}
                                value={partialCloseLots}
                                onChange={e => setPartialCloseLots(e.target.value)}
                                placeholder={t('enterLotsToClose')}
                                className="w-full bg-background/50 border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            <div className="text-xs text-muted-foreground mt-2">
                                {t('maximum')}:{' '}
                                {Number(selectedTradeToPartialClose.lot_size || 0).toFixed(2)}{' '}
                                {t('lots')}
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setPartialCloseDialogOpen(false);
                                    setSelectedTradeToPartialClose(null);
                                    setPartialCloseLots('');
                                }}
                                className="flex-1 py-3 trader-glass hover:bg-foreground/10 rounded-lg font-medium transition-colors"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={confirmPartialClose}
                                disabled={
                                    partialCloseTradeMutation.isPending ||
                                    !partialCloseLots ||
                                    parseFloat(partialCloseLots) <= 0
                                }
                                className="flex-1 py-3 trader-fab-gradient text-white hover:opacity-90 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {partialCloseTradeMutation.isPending
                                    ? t('closing')
                                    : t('confirmPartialClose')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileTradingPage;
